import { useEffect, useState } from "react";

import { AppDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { showToast } from "@/lib/utils/toast";
import { useAuthStore } from "@/features/auth";

import { onboardingService } from "../services";

const DEFAULT_SUBJECT = "Meeting request to complete project setup";
const DEFAULT_MESSAGE =
  "I'd like to schedule a consultation to discuss the project setup. Could you please share the time slots you're available over the next week to complete the project setup?";

const MeetingRequestDialog = ({ open, onOpenChange, user, onArranged }) => {
  const { user: authUser } = useAuthStore();
  const targetUser = user || authUser || {};

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(DEFAULT_SUBJECT);
      setMessage(DEFAULT_MESSAGE);
      setEmailErr("");
    }
  }, [open]);

  const isValidEmail = (value) => /.+@.+\..+/.test(String(value || "").trim());

  const submit = async () => {
    if (!isValidEmail(targetUser?.email)) {
      setEmailErr("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      recipientEmail: targetUser.email,
      recipientName: targetUser.username || targetUser.name || targetUser.email,
      subject: subject || DEFAULT_SUBJECT,
      message: message || DEFAULT_MESSAGE,
      userId: targetUser.id || targetUser._id || targetUser.userId,
    };

    try {
      const { success } = await onboardingService.sendMeetingRequest(payload, {
        showErrorToast: false,
      });
      if (!success) {
        throw new Error("Failed to send meeting request. Please try again.");
      }
      showToast.success("Meeting request sent successfully!");
      onArranged?.({ email: targetUser.email, subject, message });
      onOpenChange?.(false);
    } catch (error) {
      showToast.error(
        error?.message || "Failed to send meeting request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Request a meeting"
      description="Let the team know when you want to meet to complete onboarding."
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>To</Label>
          <div className="text-sm text-muted-foreground">
            {targetUser?.email || "No email available"}
          </div>
          {emailErr && <p className="text-xs text-destructive">{emailErr}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="meeting-subject">Subject</Label>
          <Input
            id="meeting-subject"
            type="text"
            placeholder="Meeting request to complete project setup"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="meeting-message">Message</Label>
          <Textarea
            id="meeting-message"
            rows={8}
            placeholder="Hi, I would like to arrange a meeting..."
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </div>
      </div>
    </AppDialog>
  );
};

export default MeetingRequestDialog;
